import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { NewChatPage } from "../../../components/chats/new-chat-page";

/**
 * What a new chat's address carries: `prompt`, the words its box starts with, such as a teammate's
 * New playbook naming the teammate (nothing is sent until the person sends it), and `record`, the
 * desktop tray's Start a recording when the page was not listening for it (an installed app sends
 * `/chats?new=true&record=true`, which leads here): the page starts it as Show on screen does. An
 * old link that asked to bring an earlier chat in (`context`, `branch`) opens a plain new chat.
 */
const newChatSearchSchema = z.object({
  prompt: z.string().max(500).optional().catch(undefined),
  record: z.boolean().optional().catch(undefined),
});

/** A new chat, as a page of its own: where the conversation it starts will be. */
export const Route = createFileRoute("/_authenticated/_workspace/chats/new")({
  validateSearch: newChatSearchSchema,
  // What the address no longer means is taken out of it, so it is not carried on or bookmarked.
  beforeLoad: ({ location, search }) => {
    const raw = new URLSearchParams(location.searchStr);
    if (raw.has("branch") || raw.has("context")) {
      // By the whole address: a redirect to this route by its search would keep what it does not know.
      const kept = new URLSearchParams();
      if (search.prompt) kept.set("prompt", search.prompt);
      if (search.record) kept.set("record", "true");
      const query = kept.toString();
      throw redirect({ href: query ? `/chats/new?${query}` : "/chats/new", replace: true });
    }
  },
  component: function NewChatRoute() {
    const { prompt, record } = Route.useSearch();
    // Asked again with other words, the page starts over from them.
    return (
      <NewChatPage
        key={prompt ?? ""}
        {...(prompt === undefined ? {} : { prompt })}
        startRecording={record === true}
      />
    );
  },
});
